import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";

// Completed project images for animated collage
const projectImages = [
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/q8mo949w_Reception%202.jpg",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/xcpji8p2_Reception%201.jpg",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/ji227wsb_Reception%203.jpg",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/yn1u5djz_Reception%204.jpg",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/rt9kpxr1_IMG_1372.JPEG",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/470yltz4_Image%201.jpg",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/w44tgcld_3d%20visual%201.jpg",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/j8s20asd_Render%2002%20%281%29.png",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/cqr3d5x8_PRES%201.jpeg",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/msd1hq3d_PRES%202.jpeg",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/fxcrptyl_PRES%203.jpeg",
  "https://customer-assets.emergentagent.com/job_nh-spaces/artifacts/xwukak1t_PRES%204.jpeg",
];

const TEAM_IMAGE = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80";

// Animated Photo Collage Component for Story Section
const AnimatedCollage = () => {
  const [activeIndices, setActiveIndices] = useState([0, 1, 2, 3]);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndices(prev => {
        const newIndices = [...prev];
        const randomPosition = Math.floor(Math.random() * 4);
        const availableImages = projectImages.filter((_, i) => !newIndices.includes(i));
        if (availableImages.length > 0) {
          const randomImageIndex = projectImages.indexOf(
            availableImages[Math.floor(Math.random() * availableImages.length)]
          );
          newIndices[randomPosition] = randomImageIndex;
        }
        return newIndices;
      });
    }, 2500);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="aspect-[4/5] grid grid-cols-2 grid-rows-2 gap-2 overflow-hidden">
      {activeIndices.map((imageIndex, position) => (
        <div 
          key={position} 
          className="relative overflow-hidden"
        >
          <img
            src={projectImages[imageIndex]}
            alt={`Project ${imageIndex + 1}`}
            className="w-full h-full object-cover transition-all duration-1000 ease-in-out hover:scale-105"
          />
        </div>
      ))}
    </div>
  );
};

const stats = [
  { number: "11", suffix: "", label: "Years in Business" },
  { number: "21", suffix: "", label: "Years Experience" },
  { number: "200", suffix: "+", label: "Projects Completed" },
];

const values = [
  {
    title: "Personal Touch",
    description:
      "Every client is unique, and so is every project. We invest time in understanding your specific needs, preferences, and lifestyle.",
  },
  {
    title: "Uncompromising Quality",
    description:
      "From materials to craftsmanship, we never cut corners. Our trusted supplier network ensures premium results every time.",
  },
  {
    title: "Collaborative Partnership",
    description:
      "We work alongside you throughout the entire process, ensuring your vision is realized while providing expert guidance.",
  },
  {
    title: "Timeless Design",
    description:
      "We create spaces that endure—designs that feel as relevant and beautiful in ten years as they do today.",
  },
];

const timeline = [
  {
    year: "2015",
    title: "Founded in Cape Town",
    description:
      "NH Interiors was established with a vision to provide personalized, high-quality interior design services.",
  },
  {
    year: "2018",
    title: "Corporate Expansion",
    description:
      "Began specializing in corporate office design, building relationships with leading businesses.",
  },
  {
    year: "2021",
    title: "Turnkey Services",
    description:
      "Expanded offerings to include complete turnkey solutions with trusted construction partners.",
  },
  {
    year: "2024",
    title: "National & International Reach",
    description:
      "Enhanced our portfolio with projects across South Africa and internationally, including Mozambique.",
  },
];

// Project locations for the animated map with reference numbers
const projectLocations = [
  { name: "Johannesburg", x: 72, y: 38, ref: 1 },
  { name: "Durban", x: 82, y: 55, ref: 2 },
  { name: "Cape Town", x: 38, y: 82, ref: 3 },
  { name: "Port Elizabeth", x: 68, y: 72, ref: 4 },
  { name: "Kimberley", x: 56, y: 42, ref: 5 },
  { name: "George", x: 48, y: 80, ref: 6 },
  { name: "Paarl", x: 34, y: 76, ref: 7 },
  { name: "Franschhoek", x: 40, y: 74, ref: 8 },
  { name: "Mozambique", x: 90, y: 25, ref: 9 },
  { name: "Stellenbosch", x: 36, y: 78, ref: 10 },
  { name: "East London", x: 74, y: 67, ref: 11 },
];

// Animated Map Component
const AnimatedMap = () => {
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [animatedPins, setAnimatedPins] = useState([]);

  useEffect(() => {
    // Stagger the pin animations
    projectLocations.forEach((_, index) => {
      setTimeout(() => {
        setAnimatedPins(prev => [...prev, index]);
      }, index * 200);
    });
  }, []);

  return (
    <div className="relative w-full aspect-square bg-soft-beige rounded-sm overflow-hidden" data-testid="animated-map">
      {/* Simplified Africa/South Africa outline using SVG */}
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {/* Simplified South Africa shape — wider spread */}
        <path
          d="M30 70 Q38 55, 55 48 Q72 42, 82 30 Q88 38, 85 52 Q82 65, 76 74 Q66 82, 52 84 Q40 86, 33 80 Q28 76, 30 70"
          fill="none"
          stroke="#8B7355"
          strokeWidth="0.4"
          className="opacity-25"
        />
        {/* Mozambique indication */}
        <path
          d="M85 25 Q92 20, 93 30 Q91 42, 85 48"
          fill="none"
          stroke="#8B7355"
          strokeWidth="0.4"
          className="opacity-25"
        />
      </svg>

      {/* Location Pins with Numbers */}
      {projectLocations.map((location, index) => (
        <div
          key={location.name}
          className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ${
            animatedPins.includes(index) ? "opacity-100 scale-100" : "opacity-0 scale-0"
          }`}
          style={{ left: `${location.x}%`, top: `${location.y}%` }}
          onMouseEnter={() => setHoveredLocation(index)}
          onMouseLeave={() => setHoveredLocation(null)}
        >
          {/* Pulsing ring */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 bg-warmBrown/15 rounded-full animate-ping" />
          </div>
          
          {/* Pin with Number */}
          <div className="relative z-10 w-8 h-8 bg-warmBrown rounded-full shadow-lg cursor-pointer hover:scale-125 transition-transform duration-300 flex items-center justify-center">
            <span className="text-white text-[10px] font-bold font-body">{location.ref}</span>
          </div>
          
          {/* Location label on hover */}
          <div 
            className={`absolute left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap transition-all duration-300 ${
              hoveredLocation === index ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
            }`}
          >
            <span className="bg-white px-3 py-1 text-xs font-body text-black shadow-lg">
              {location.name}
            </span>
          </div>
        </div>
      ))}

      {/* Map title */}
      <div className="absolute bottom-4 left-4">
        <p className="text-xs text-black/40 font-body uppercase tracking-wider">
          Project Locations
        </p>
      </div>
    </div>
  );
};

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-24">
      {/* Stats Section */}
      <section className="py-16 bg-primary" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="font-heading text-4xl md:text-6xl text-champagne mb-2">
                  {stat.number}<span className="text-3xl md:text-5xl">{stat.suffix}</span>
                </div>
                <div className="text-white/70 text-xs md:text-sm uppercase tracking-widest font-body">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-cream" data-testid="story-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
                Who We Are
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-black mb-6">
                More Than Designers—
                <br />
                <span className="italic text-warmBrown">Your Partners</span>
              </h2>
              <div className="space-y-6 text-black/60 font-body leading-relaxed">
                <p>
                  Founded in 2015, NH Interiors has spent over a decade
                  perfecting the art of interior design. What began as a passion
                  for creating beautiful spaces has grown into a respected
                  boutique studio serving both corporate and residential clients
                  across South Africa and beyond.
                </p>
                <p>
                  Our growth has been entirely organic—built through word-of-mouth
                  referrals from satisfied clients. This speaks to our commitment
                  to not just meeting expectations, but exceeding them in every
                  project we undertake.
                </p>
                <p>
                  Based in Cape Town, we bring a fresh perspective influenced by
                  our city's unique blend of natural beauty, diverse cultures,
                  and innovative spirit. Our designs balance international
                  sophistication with local warmth and character.
                </p>
              </div>
            </div>
            <div className="relative">
              <AnimatedCollage />
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="section-padding bg-soft-beige" data-testid="approach-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
              Our Philosophy
            </p>
            <h2 className="font-heading text-3xl md:text-5xl text-black mb-6">
              Clients, Not Numbers
            </h2>
            <p className="text-black/60 font-body leading-relaxed text-lg">
              In an industry often marked by impersonal service and
              one-size-fits-all solutions, we choose a different path. At NH
              Interiors, you're not a project number—you're a valued partner in
              the creative process.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 divide-x divide-black/5">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="text-center px-6 py-8"
                data-testid={`value-card-${index}`}
              >
                <span className="text-warmBrown/30 font-heading text-5xl">0{index + 1}</span>
                <h3 className="font-heading text-xl text-black mt-4 mb-4">
                  {value.title}
                </h3>
                <p className="text-black/50 text-sm font-body leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Locations Map Section */}
      <section className="section-padding bg-cream" data-testid="map-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
                Our Reach
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-black mb-6">
                Projects Across
                <br />
                <span className="italic text-warmBrown">Southern Africa</span>
              </h2>
              <p className="text-black/60 font-body leading-relaxed mb-8">
                While based in Cape Town, our portfolio extends across South Africa
                and internationally. From Johannesburg to Mozambique, we bring our
                signature design approach to clients wherever they are.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {projectLocations.map((location) => (
                  <div key={location.name} className="flex items-center gap-3">
                    <span className="w-5 h-5 bg-warmBrown rounded-full flex items-center justify-center text-white text-xs font-bold font-body">
                      {location.ref}
                    </span>
                    <span className="text-black/70 font-body text-sm">{location.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <AnimatedMap />
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-padding bg-soft-beige" data-testid="timeline-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
              Our Journey
            </p>
            <h2 className="font-heading text-3xl md:text-5xl text-black">
              Over a Decade of
              <br />
              <span className="italic text-warmBrown">Excellence</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {timeline.map((item, index) => (
              <div
                key={item.title}
                className="mb-12 last:mb-0 opacity-0 animate-fade-in border-l border-black/10 pl-8 pb-12 last:pb-0"
                style={{ animationDelay: `${index * 150}ms`, animationFillMode: 'forwards' }}
                data-testid={`timeline-item-${index}`}
              >
                <h3 className="font-heading text-xl text-black mb-2">
                  {item.title}
                </h3>
                <p className="text-black/50 font-body leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Network Section */}
      <section className="section-padding bg-cream" data-testid="network-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
                Our Network
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-black mb-6">
                Trusted Partnerships,
                <br />
                <span className="italic text-warmBrown">Premium Results</span>
              </h2>
              <p className="text-black/60 font-body leading-relaxed mb-8">
                Over the years, we've cultivated relationships with the finest
                suppliers, craftsmen, and contractors in the industry. These
                long-standing partnerships allow us to deliver exceptional
                quality consistently, whether it's custom furniture, premium
                materials, or flawless execution.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center gap-4 text-black/70 font-body">
                  <span className="w-2 h-2 bg-warmBrown" />
                  Premium material suppliers
                </li>
                <li className="flex items-center gap-4 text-black/70 font-body">
                  <span className="w-2 h-2 bg-warmBrown" />
                  Skilled craftsmen and artisans
                </li>
                <li className="flex items-center gap-4 text-black/70 font-body">
                  <span className="w-2 h-2 bg-warmBrown" />
                  Trusted construction partners
                </li>
                <li className="flex items-center gap-4 text-black/70 font-body">
                  <span className="w-2 h-2 bg-warmBrown" />
                  Custom furniture manufacturers
                </li>
              </ul>
            </div>
            <div className="bg-white p-12 border border-black/5 shadow-lg">
              <blockquote className="font-accent text-2xl md:text-3xl text-black italic leading-relaxed mb-6">
                "Our success is built on trust—trust from our clients and trust
                in our partners. These relationships are everything."
              </blockquote>
              <p className="text-warmBrown font-body text-sm uppercase tracking-wider">
                — Nadine Harris
              </p>

              <div className="border-t border-black/10 mt-8 pt-8">
                <blockquote className="font-accent text-xl md:text-2xl text-black italic leading-relaxed mb-6">
                  "Design should never feel exclusive—it should feel personal.
                  True design lies in understanding the soul of a space and the
                  people within it, creating something intentional, accessible,
                  and deeply connected."
                </blockquote>
                <p className="text-warmBrown font-body text-sm uppercase tracking-wider">
                  — Joshua De Sousa Coutinho
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-warmBrown" data-testid="about-cta-section">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl md:text-5xl text-white mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-white/80 font-body text-lg max-w-xl mx-auto mb-8">
            Let's discuss your project and discover how NH Interiors can bring
            your vision to life.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-white text-black hover:bg-champagne transition-all duration-500 uppercase tracking-[0.2em] text-xs px-8 py-4 font-semibold"
            data-testid="about-cta-button"
          >
            Get in Touch
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
